import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Github, Linkedin, BookOpen, Download } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { portfolioData } from '../data/mock';

const Contact = () => {
  const { toast } = useToast();
  const { personal } = portfolioData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Message Sent!",
          description: data.message || "Thank you for reaching out. I'll get back to you soon!",
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadResume = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${BACKEND_URL}/api/resume/download`;
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: personal.email, href: `mailto:${personal.email}` },
    { icon: Phone, label: 'Phone', value: personal.phone, href: `tel:${personal.phone}` },
    { icon: MapPin, label: 'Location', value: personal.location, href: null }
  ];

  const socialLinks = [
    { icon: Github, label: 'GitHub', href: personal.github },
    { icon: Linkedin, label: 'LinkedIn', href: personal.linkedin },
    { icon: BookOpen, label: 'Medium', href: personal.medium }
  ];

  return (
    <section id="contact" className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
      <div className="container mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">
            Let's <span className="text-accent">Connect</span>
          </h2>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Info Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                const content = (
                  <div
                    className="p-4 rounded-xl border transition-all"
                    style={{
                      background: 'var(--bg-card)',
                      borderColor: 'var(--border)'
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                        <Icon className="h-5 w-5" style={{ color: '#000' }} />
                      </div>
                      <div>
                        <p className="text-sm" style={{
                          color: 'var(--text-muted)',
                          fontFamily: 'JetBrains Mono, monospace'
                        }}>{info.label}</p>
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{info.value}</p>
                      </div>
                    </div>
                  </div>
                );

                return info.href ? (
                  <a key={index} href={info.href} target="_blank" rel="noopener noreferrer">
                    {content}
                  </a>
                ) : (
                  <div key={index}>{content}</div>
                );
              })}
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon-btn"
                    title={social.label}
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>

            {/* Download Resume */}
            <button
              onClick={handleDownloadResume}
              className="action-btn w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2"
              style={{
                background: 'var(--accent)',
                color: '#000',
                fontFamily: 'JetBrains Mono, monospace'
              }}
            >
              <Download className="h-5 w-5" />
              Download Resume
            </button>
          </div>

          {/* Contact Form */}
          <div
            className="p-8 rounded-2xl border"
            style={{
              background: 'var(--bg-card)',
              borderColor: 'var(--border)'
            }}
          >
            <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Send Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block mb-2 text-sm" style={{
                  color: 'var(--text-muted)',
                  fontFamily: 'JetBrains Mono, monospace'
                }}>
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block mb-2 text-sm" style={{
                  color: 'var(--text-muted)',
                  fontFamily: 'JetBrains Mono, monospace'
                }}>
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div>
                <label htmlFor="subject" className="block mb-2 text-sm" style={{
                  color: 'var(--text-muted)',
                  fontFamily: 'JetBrains Mono, monospace'
                }}>
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="Project inquiry"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div>
                <label htmlFor="message" className="block mb-2 text-sm" style={{
                  color: 'var(--text-muted)',
                  fontFamily: 'JetBrains Mono, monospace'
                }}>
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Your message..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="action-btn w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2"
                style={{
                  background: 'var(--accent)',
                  color: '#000',
                  fontFamily: 'JetBrains Mono, monospace'
                }}
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send className="h-5 w-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
